import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
} from 'react';
import {
  InlineEditor,
  type InlineEditorInstance,
  type InlineEditorProps,
} from '@page/core/components/editor/InlineEditor';
import { cn } from '../../lib';
import { Button, Input } from '../../designSystem';

type ExpandableInputEditorComponent = ForwardRefExoticComponent<
  InlineEditorProps & RefAttributes<InlineEditorInstance>
>;

export interface ExpandableInputProps {
  content: string;
  onContentChange: (content: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  isSaveDisabled?: boolean;
  autofocus?: boolean;
  placeholder?: string;
  collapsed?: boolean;
  collapsedPlaceholder?: React.ReactNode;
  onCollapsedChange?: (collapsed: boolean) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  toolbar?: React.ReactNode;
  saveButtonLabel?: string;
  cancelButtonLabel?: string;
  expandedClassName?: string;
  EditorComponent?: ExpandableInputEditorComponent;
  editorProps?: Partial<InlineEditorProps>;
}

export type ExpandableInputImperativeMethods = {
  focus: () => void;
};

const ExpandableInputBase = (
  {
    content,
    onContentChange,
    onSave,
    onCancel,
    isLoading = false,
    isSaveDisabled = false,
    autofocus = false,
    placeholder = 'Add your comment...',
    collapsed,
    collapsedPlaceholder,
    onCollapsedChange,
    prefix,
    suffix,
    toolbar,
    saveButtonLabel = 'Save',
    cancelButtonLabel = 'Cancel',
    expandedClassName,
    EditorComponent = InlineEditor,
    editorProps,
  }: ExpandableInputProps,
  ref: React.Ref<ExpandableInputImperativeMethods>,
) => {
  const collapsedInputRef = useRef<HTMLInputElement>(null);

  const handleStartFocusing = useCallback(() => {
    onCollapsedChange?.(false);
    onContentChange(' ');
  }, [onCollapsedChange, onContentChange]);

  const focusImperativeHandle = useCallback(() => {
    if (collapsed) {
      collapsedInputRef.current?.focus();
    }
  }, [collapsed]);

  const editorRef = useRef<InlineEditorInstance>(null);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;

    // If focus is moving to an element outside this component, don't recapture.
    // This prevents stealing focus from popovers, menus, and other UI elements.
    if (relatedTarget && !e.currentTarget.contains(relatedTarget)) return;

    requestAnimationFrame(() => {
      editorRef.current?.editor?.editing.view.focus();
    });
  }, []);

  useImperativeHandle(ref, () => ({
    focus: focusImperativeHandle,
  }));

  if (collapsed && collapsedPlaceholder) {
    return collapsedPlaceholder;
  }

  if (collapsed) {
    return (
      <div className="p-4 border-t border-border-default">
        <Input
          size="md"
          placeholder={placeholder}
          className="w-full"
          onFocus={handleStartFocusing}
          ref={collapsedInputRef}
        />
      </div>
    );
  }

  return (
    <div onBlur={handleBlur} className={cn('flex flex-col h-full')}>
      <div
        className={cn(
          'normalized-markdown p-4 gap-4 h-[calc(100%-136px)] flex flex-col relative overflow-y-auto overflow-x-hidden [&_.ck.ck-content]:min-h-[80px]! flex-1',
          expandedClassName,
        )}
      >
        {prefix}

        <EditorComponent
          ref={editorRef}
          data={content}
          onDataChange={onContentChange}
          autofocus={autofocus}
          readonly={isLoading}
          data-draggable="false"
          showToolbar="hasValue"
          toolbarOffset={{
            y: 8,
          }}
          {...editorProps}
        />

        {!content.trim() ? (
          <div className="absolute top-4 left-4">
            <div className="text-text-tertiary font-body-md">{placeholder}</div>
          </div>
        ) : null}

        {suffix}

        {toolbar}
      </div>

      <div className="p-4 border-t border-border-default">
        <div className="flex items-center justify-end gap-2">
          <Button size="lg" variant="secondary" onClick={onCancel} disabled={isLoading} data-draggable="false">
            {cancelButtonLabel}
          </Button>

          <Button size="lg" onClick={onSave} loading={isLoading} data-draggable="false" disabled={isSaveDisabled}>
            {saveButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ExpandableInput = memo(forwardRef(ExpandableInputBase));
